/**
 * AiConsentManagerModal — "AI processing" under Settings → Privacy & Security.
 *
 * Lists the AI-processing consents currently granted for this account and
 * lets the person withdraw any one of them (GDPR Art. 7(3): withdrawal must
 * be as easy as giving consent). Covers every type a person can actually be
 * asked about in-app: the five D3 gate types (`AiConsentType`) PLUS `voice` —
 * `VoiceConsentSheet` has granted it since before this workstream but never
 * had a withdrawal path anywhere in the app, which is the same dead end D3
 * fixes for the others.
 *
 * GROUPED BY TYPE, not by row. `consent_records` is an append-only audit
 * trail — a type can have more than one ACTIVE row (a stale auto-granted
 * `1.0` `ai_scoring` row from before D2 removed the auto-grant, sitting
 * alongside a genuine `1.1` row; or two rows from a double-submit race).
 * Listing rows individually let a founder "withdraw" one and see the type
 * still work, because the OTHER active row was still there — consent stayed
 * in force while the UI said it was gone. So: one entry per type, "granted"
 * only when a row exists at that type's CURRENT version, and "Withdraw"
 * clears EVERY active row of that type in one update, whatever version each
 * one is at.
 *
 * Withdrawing goes through the owner's own UPDATE (`consent_records` RLS
 * policy "Users can withdraw consent": USING/WITH CHECK auth.uid() =
 * user_id — confirmed against `supabase/tests/consent_records_rls_test.sql`
 * before writing this). The NEXT gated AI action then shows the consent
 * sheet again, exactly like a founder who never consented in the first
 * place.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text } from 'react-native';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, AlertCircle } from 'lucide-react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { AppModal, ModalAction } from '@/components/modal';
import { useThemeColors } from '@/lib/useThemeColors';
import { useAuth } from '@/lib/supabase/auth';
import { supabase, isSupabaseConfigured } from '@/lib/supabase/client';
import { mode } from '@/lib/config/app-mode';
import { getUserMessage, handleError, reportError } from '@/lib/errors';
import { AI_CONSENT_VERSIONS, type AiConsentType } from '@/lib/consent/ai-consent';
import { VOICE_CONSENT_VERSION } from '@/components/interview/VoiceConsentSheet';
import { APP_LOCALE } from '@/lib/format';

/** Every type a person can be asked to consent to in this app. */
type ManagedConsentType = AiConsentType | 'voice';

const MANAGED_CONSENT_TYPES: ManagedConsentType[] = [
  'ai_scoring',
  'pitch_deck_analysis',
  'startup_insights',
  'strategic_analysis',
  'team_extraction',
  'voice',
];

const CONSENT_LABELS: Record<ManagedConsentType, string> = {
  ai_scoring: 'AI investment scoring',
  pitch_deck_analysis: 'Pitch deck analysis',
  startup_insights: 'Startup Insights advisor',
  strategic_analysis: 'Strategic analysis',
  team_extraction: 'Team info extraction',
  voice: 'Voice transcription',
};

/** The version a row of this type must carry to count as a live grant. */
function currentVersionOf(type: ManagedConsentType): string {
  return type === 'voice' ? VOICE_CONSENT_VERSION : AI_CONSENT_VERSIONS[type];
}

interface RawConsentRow {
  consent_type: ManagedConsentType;
  consent_version: string;
  granted_at: string;
}

interface GrantedType {
  type: ManagedConsentType;
  /** Most recent grant among this type's active rows — what "Granted" shows. */
  grantedAt: string;
}

function formatGrantedDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(APP_LOCALE, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

interface AiConsentManagerModalProps {
  visible: boolean;
  onClose: () => void;
}

export function AiConsentManagerModal({ visible, onClose }: AiConsentManagerModalProps) {
  const colors = useThemeColors();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const queryClient = useQueryClient();
  const queryKey = ['ai-consent-manager', userId] as const;

  const [selectedType, setSelectedType] = useState<ManagedConsentType | null>(null);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);

  const {
    data: rawRows,
    isLoading,
    isError,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey,
    enabled: !!userId && visible,
    // THROWS rather than swallowing to `[]` — this is the one screen whose
    // purpose is withdrawal, so a read failure rendering as "you have not
    // granted any AI-processing consent yet" would be a FALSE statement, not
    // a safe fallback. `isError`/`queryError` below render the actual
    // failure instead.
    queryFn: async (): Promise<RawConsentRow[]> => {
      if (!userId) return [];
      if (mode.isMock || !isSupabaseConfigured()) return [];
      const { data, error } = await supabase
        .from('consent_records')
        .select('consent_type, consent_version, granted_at')
        .eq('user_id', userId)
        .in('consent_type', MANAGED_CONSENT_TYPES)
        .eq('granted', true)
        .is('withdrawn_at', null)
        .order('granted_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as RawConsentRow[];
    },
  });

  // react-query v5 dropped per-query `onError` — report once per failure
  // here rather than inside `queryFn` (which would re-report on every
  // internal retry) or on every render (`getUserMessage` below is pure and
  // safe to call each render; reporting is not).
  useEffect(() => {
    if (queryError) reportError(queryError, { context: 'AiConsentManagerModal.query', silent: true });
  }, [queryError]);

  // GROUPED, and filtered to CURRENT-version rows only — a type with only a
  // stale-version active row (e.g. a pre-D2 auto-granted `1.0` ai_scoring
  // row) is not something the founder actually agreed to under today's
  // copy, so it is not listed as granted here even though it is still
  // technically "active" server-side (and would still need clearing if it
  // exists — see `withdraw` below, which clears every active row of a type
  // regardless of version).
  const grantedTypes: GrantedType[] = useMemo(() => {
    const rows = rawRows ?? [];
    const result: GrantedType[] = [];
    for (const type of MANAGED_CONSENT_TYPES) {
      const current = rows.find((r) => r.consent_type === type && r.consent_version === currentVersionOf(type));
      if (current) result.push({ type, grantedAt: current.granted_at });
    }
    return result;
  }, [rawRows]);

  // AppModal never unmounts this component — reset local state whenever the
  // modal closes, so reopening it never shows a stale selection or a stale
  // error from a previous visit.
  const close = useCallback(() => {
    setSelectedType(null);
    setWithdrawError(null);
    onClose();
  }, [onClose]);

  const withdraw = useMutation({
    mutationFn: async (type: ManagedConsentType) => {
      if (!userId) return;
      // EVERY active row of this type, not just the current-version one —
      // a duplicate or stale-version row left active is exactly what makes
      // a per-row withdrawal misleading (see the module doc).
      const { error } = await supabase
        .from('consent_records')
        .update({ withdrawn_at: new Date().toISOString(), withdrawal_reason: 'user_withdrew_in_app' })
        .eq('user_id', userId)
        .eq('consent_type', type)
        .is('withdrawn_at', null);
      if (error) throw error;
    },
    onSuccess: () => {
      setSelectedType(null);
      setWithdrawError(null);
      queryClient.invalidateQueries({ queryKey });
      // No `exact: true` — react-query's default array-key match is a
      // PREFIX match, so this invalidates every `['ai-consent', userId,
      // typesKey]` gate cache in one call (each gate gets re-fetched fresh
      // the next time it is consulted), not just one.
      queryClient.invalidateQueries({ queryKey: ['ai-consent'] });
      queryClient.invalidateQueries({ queryKey: ['voice-consent'] });
    },
    onError: (err) => {
      const { message } = handleError(err, { context: 'AiConsentManagerModal.withdraw' });
      setWithdrawError(message);
    },
  });

  return (
    <AppModal
      visible={visible}
      onClose={close}
      title="AI processing"
      footer={
        <AppModal.Actions>
          <ModalAction label="Close" variant="neutral" onPress={close} />
          <ModalAction
            label="Withdraw consent"
            variant="destructive"
            disabled={!selectedType || withdraw.isPending}
            onPress={() => selectedType && withdraw.mutate(selectedType)}
          />
        </AppModal.Actions>
      }
    >
      <Text className="text-[14px] leading-5 mb-4" style={{ color: colors.text.secondary }}>
        These are the AI-processing permissions you have granted. Select one and withdraw it at any
        time — the related feature will ask you to consent again the next time you use it.
      </Text>

      {withdrawError && (
        <View
          className="flex-row items-start gap-2 rounded-xl px-3 py-3 mb-3"
          style={{ backgroundColor: colors.red.light }}
        >
          <AlertCircle size={16} color={colors.error} style={{ marginTop: 1 }} />
          <Text className="flex-1 text-[13px] leading-5" style={{ color: colors.error }}>
            {withdrawError}
          </Text>
        </View>
      )}

      {isLoading && (
        <Text className="text-[13px]" style={{ color: colors.text.tertiary }}>
          Loading…
        </Text>
      )}

      {/* A read FAILURE is not the same claim as "nothing granted" — this is
          the one screen whose purpose is withdrawal, so conflating "we
          couldn't check" with "there is nothing to withdraw" would be a
          false statement of protection that isn't there. */}
      {!isLoading && isError && (
        <View
          className="rounded-xl px-3 py-3 mb-3"
          style={{ backgroundColor: colors.red.light }}
        >
          <View className="flex-row items-start gap-2">
            <AlertCircle size={16} color={colors.error} style={{ marginTop: 1 }} />
            <Text className="flex-1 text-[13px] leading-5" style={{ color: colors.error }}>
              {getUserMessage(queryError)}
            </Text>
          </View>
          <AnimatedPressable
            onPress={() => { void refetch(); }}
            accessibilityRole="button"
            accessibilityLabel="Try loading AI processing consent again"
            className="mt-2 self-start"
          >
            <Text className="text-[13px] font-semibold" style={{ color: colors.primary[600] }}>
              Try again
            </Text>
          </AnimatedPressable>
        </View>
      )}

      {!isLoading && !isError && grantedTypes.length === 0 && (
        <Text className="text-[13px]" style={{ color: colors.text.tertiary }}>
          You have not granted any AI-processing consent yet.
        </Text>
      )}

      {!isLoading && !isError && grantedTypes.map(({ type, grantedAt }) => {
        const selected = type === selectedType;
        return (
          <AnimatedPressable
            key={type}
            onPress={() => setSelectedType(selected ? null : type)}
            accessibilityRole="button"
            accessibilityLabel={`${CONSENT_LABELS[type]}, granted ${formatGrantedDate(grantedAt)}`}
            accessibilityState={{ selected }}
            className="flex-row items-center rounded-xl px-3 py-3 mb-2"
            style={{
              backgroundColor: selected ? colors.primary[500] + '14' : colors.surface.secondary,
              borderWidth: 1,
              borderColor: selected ? colors.primary[500] : colors.border.light,
            }}
          >
            <View className="flex-1">
              <Text numberOfLines={1} className="text-[14px] font-medium" style={{ color: colors.text.primary }}>
                {CONSENT_LABELS[type]}
              </Text>
              <Text numberOfLines={1} className="text-[12px] mt-0.5" style={{ color: colors.text.tertiary }}>
                Granted {formatGrantedDate(grantedAt)}
              </Text>
            </View>
            {selected && (
              <View
                className="w-6 h-6 rounded-full items-center justify-center ml-2 shrink-0"
                style={{ backgroundColor: colors.primary[600] }}
              >
                <Check size={14} color={colors.text.inverse} />
              </View>
            )}
          </AnimatedPressable>
        );
      })}
    </AppModal>
  );
}
